import { useQuery } from '@tanstack/react-query';
import { X, Eye, Heart, MessageCircle, Share2, Clapperboard } from 'lucide-react';
import { storiesApi } from '@/api/stories';

/**
 * Analytics для автора стори — totals + гистограмма retention по 10% бакетам.
 * Бакеты рисуем обычным div'ом, без D3 / recharts (минимум зависимостей).
 */
export default function StoryAnalyticsModal({ storyId, onClose }: { storyId: string; onClose: () => void }) {
  const { data, isLoading, error } = useQuery({
    queryKey: ['story', storyId, 'analytics'],
    queryFn: () => storiesApi.getAnalytics(storyId),
    refetchInterval: 30_000, // свежее каждые 30 сек
  });

  const maxBucket = Math.max(1, ...(data?.retention.map((r) => r.count) ?? [0]));

  return (
    <div className="fixed inset-0 z-[70] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4">
      <div className="relative w-full max-w-lg bg-surface-900 border border-surface-700 rounded-xl overflow-hidden">
        <div className="flex items-center justify-between px-5 py-4 border-b border-surface-800">
          <h3 className="font-semibold text-surface-50">Аналитика стори</h3>
          <button onClick={onClose} className="text-surface-400"><X size={18} /></button>
        </div>

        <div className="p-5 space-y-4">
          {isLoading && <div className="text-center text-surface-400">Загрузка…</div>}
          {error && <div className="text-red-400 text-sm">Ошибка загрузки аналитики</div>}

          {data && (
            <>
              <div className="grid grid-cols-5 gap-2 text-center">
                <Stat icon={<Eye size={14} />} value={data.totals.views} label="Просмотры" />
                <Stat icon={<Heart size={14} />} value={data.totals.likes} label="Лайки" />
                <Stat icon={<MessageCircle size={14} />} value={data.totals.comments} label="Комменты" />
                <Stat icon={<Share2 size={14} />} value={data.totals.shares} label="Шары" />
                <Stat icon={<Clapperboard size={14} />} value={data.totals.replies} label="Дуэты" />
              </div>

              <div className="bg-surface-800 rounded-lg p-4">
                <div className="flex items-center justify-between mb-3 text-xs text-surface-400">
                  <span>Досматриваемость</span>
                  <span>Ср. {Math.round(data.avgCompletionPct)}%</span>
                </div>
                <div className="flex items-end gap-1 h-32">
                  {data.retention.map((b) => (
                    <div key={b.bucketStartPct} className="flex-1 flex flex-col items-center justify-end">
                      <div
                        className="w-full bg-brand-500 rounded-t"
                        style={{ height: `${(b.count / maxBucket) * 100}%`, minHeight: b.count ? 2 : 0 }}
                        title={`${b.bucketStartPct}–${b.bucketStartPct + 10}% · ${b.count} зрителей`}
                      />
                      <div className="text-[9px] text-surface-500 mt-1">{b.bucketStartPct}</div>
                    </div>
                  ))}
                </div>
                <p className="text-[10px] text-surface-500 mt-2">
                  X — % длины стори, Y — сколько зрителей досмотрели минимум до этой точки
                </p>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function Stat({ icon, value, label }: { icon: React.ReactNode; value: number; label: string }) {
  return (
    <div>
      <div className="flex items-center justify-center gap-1 text-surface-400 text-xs">{icon}{label}</div>
      <div className="text-xl font-bold text-surface-50 mt-1">{value}</div>
    </div>
  );
}
